// ============================================================================
// Safe Care - Génération de fichiers d'export en mémoire (Blob)
// Module : features/assistance_sociale/services
//
// Au lieu de télécharger directement (saveAs), chaque export produit un
// ExportFile { blob, name, ext, previewHtml } qui est remis à la carte animée
// <ExportFileModal /> : l'utilisateur choisit ensuite Visualiser / Plus tard.
// ============================================================================

import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, Table as DocxTable, TableCell, TableRow, TextRun, WidthType } from 'docx';

export type FormatExport = 'pdf' | 'xlsx' | 'docx';

export interface ExportFile {
  blob: Blob;
  name: string;
  ext: FormatExport;
  previewHtml?: string;
}

const esc = (v: any) =>
  String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const stamp = () => dayjs().format('YYYYMMDD_HHmm');

// --- Aperçu HTML (Excel / Word) -------------------------------------------

export const tableHtml = (data: any[]): string => {
  if (!data.length) return '<p>Aucune donnée</p>';
  const cols = Object.keys(data[0]);
  return `<table><thead><tr>${cols.map((c) => `<th>${esc(c)}</th>`).join('')}</tr></thead><tbody>${data
    .map((r) => `<tr>${cols.map((c) => `<td>${esc(r[c])}</td>`).join('')}</tr>`)
    .join('')}</tbody></table>`;
};

export const sectionsHtml = (sections: { titre: string; data: any[] }[]): string =>
  sections.map((s) => `<h3>${esc(s.titre)}</h3>${tableHtml(s.data)}`).join('');

// --- Excel -----------------------------------------------------------------

export const xlsxBlob = (sheets: { name: string; data: any[] }[]): Blob => {
  const wb = XLSX.utils.book_new();
  sheets.forEach((s) =>
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(s.data.length ? s.data : [{ Information: 'Aucune donnée' }]), s.name.substring(0, 31))
  );
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

// --- Word ------------------------------------------------------------------

const docxTable = (data: any[]) => {
  const cols = Object.keys(data[0]);
  return new DocxTable({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({
        children: cols.map(
          (col) =>
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: col, bold: true, color: 'FFFFFF' })] })],
              shading: { color: '116599', fill: '116599', type: 'clear' },
            })
        ),
      }),
      ...data.map(
        (row) =>
          new TableRow({
            children: Object.values(row).map((v) => new TableCell({ children: [new Paragraph(String(v ?? ''))] })),
          })
      ),
    ],
  });
};

export const docxBlob = async (titre: string, sections: { titre?: string; data: any[] }[]): Promise<Blob> => {
  const doc = new Document({
    sections: [
      {
        properties: { page: { size: { orientation: 'landscape' } } },
        children: [
          new Paragraph({ children: [new TextRun({ text: titre, bold: true, size: 32 })] }),
          ...sections.flatMap((sec) => [
            ...(sec.titre ? [new Paragraph({ children: [new TextRun({ text: sec.titre, bold: true, size: 24 })] })] : []),
            sec.data.length ? docxTable(sec.data) : new Paragraph('Aucune donnée'),
            new Paragraph(' '),
          ]),
        ],
      },
    ],
  });
  return Packer.toBlob(doc);
};

// --- PDF -------------------------------------------------------------------

export const pdfBlob = (
  titre: string,
  sections: { titre?: string; head: string[]; body: any[][] }[],
  orientation: 'portrait' | 'landscape' = 'landscape'
): Blob => {
  const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  doc.setFontSize(14);
  doc.text(titre, 40, 40);
  doc.setFontSize(8);
  doc.text(`Généré le ${dayjs().format('DD/MM/YYYY HH:mm')}`, 40, 54);
  let y = 70;
  sections.forEach((sec) => {
    if (!sec.body.length) return;
    if (sec.titre) {
      doc.setFontSize(10);
      doc.text(sec.titre, 40, y);
      y += 8;
    }
    autoTable(doc, {
      head: [sec.head],
      body: sec.body,
      startY: y + 8,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [17, 101, 153], textColor: 255 },
      theme: 'grid',
    });
    y = (doc as any).lastAutoTable.finalY + 20;
  });
  return doc.output('blob');
};

// --- Façades utilisées par les pages ----------------------------------------

// Export générique d'un seul tableau (remplace GlobalExportTo*).
export const singleTableFile = async (
  format: FormatExport,
  data: any[],
  titre: string,
  fileName: string
): Promise<ExportFile> => {
  const name = `${fileName}_${stamp()}`;
  const previewHtml = `<h3>${esc(titre)}</h3>${tableHtml(data)}`;
  if (format === 'xlsx') return { blob: xlsxBlob([{ name: 'Données', data }]), name: `${name}.xlsx`, ext: 'xlsx', previewHtml };
  if (format === 'docx') return { blob: await docxBlob(titre, [{ data }]), name: `${name}.docx`, ext: 'docx', previewHtml };
  const head = Object.keys(data[0]);
  return { blob: pdfBlob(titre, [{ head, body: data.map((r) => head.map((h) => r[h])) }]), name: `${name}.pdf`, ext: 'pdf' };
};

// Export multi-sections (un tableau / une feuille par section).
export const sectionsFile = async (
  format: FormatExport,
  titre: string,
  fileName: string,
  sections: { titre: string; data: any[] }[]
): Promise<ExportFile> => {
  const name = `${fileName}_${stamp()}`;
  const previewHtml = `<h3>${esc(titre)}</h3>${sectionsHtml(sections)}`;
  if (format === 'xlsx')
    return { blob: xlsxBlob(sections.map((s) => ({ name: s.titre, data: s.data }))), name: `${name}.xlsx`, ext: 'xlsx', previewHtml };
  if (format === 'docx') return { blob: await docxBlob(titre, sections), name: `${name}.docx`, ext: 'docx', previewHtml };
  return {
    blob: pdfBlob(
      titre,
      sections.map((s) => ({
        titre: s.titre,
        head: s.data.length ? Object.keys(s.data[0]) : ['Information'],
        body: s.data.map((r) => Object.values(r)),
      }))
    ),
    name: `${name}.pdf`,
    ext: 'pdf',
  };
};
